import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { ProductCategoryCreateSchema, ProductCategoryEditSchema } from "../forms/categorySchema"

import { Button } from "@/components/common/Button"
import { CancelTemplate } from "@/components/common/Modals"
import type { Category, CategoryCreateInput, CategoryUpdateInput } from "@/graphql/codegen/graphql"
import CategoryForm from "@/screens/ManageProducts/components/ProductCategories/forms/ProductCategoryForm"
import useProductCategoryActions from "@/screens/ManageProducts/components/ProductCategories/hooks/useProductCategoryActions"
import { normalizeToUpperCase } from "@/utils/utils"

interface CategoryDialogProps {
  onSuccess?: () => void
  onClose: () => void
  existingCategory?: Category
}

const defaultFormValues: CategoryCreateInput = {
  name: "",
}

const CategoryFormDialog = ({ onClose, onSuccess, existingCategory }: CategoryDialogProps): ReactElement => {
  const isCreateMode = !existingCategory
  const originalName = existingCategory?.name ?? ""

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const methods = useForm<CategoryCreateInput | CategoryUpdateInput>({
    defaultValues: isCreateMode
      ? defaultFormValues
      : {
          name: originalName,
          categoryId: existingCategory?.categoryId,
        },
    resolver: zodResolver(isCreateMode ? ProductCategoryCreateSchema : ProductCategoryEditSchema),
    mode: "onChange",
  })

  const {
    setError,
    handleSubmit,
    watch,
    formState: { isValid },
  } = methods

  const { createProductCategory, updateProductCategory } = useProductCategoryActions({
    setError,
    onSuccess,
    onClose,
  })

  const onSubmit = handleSubmit((data: CategoryCreateInput | CategoryUpdateInput) => {
    if (isCreateMode) {
      createProductCategory(data as CategoryCreateInput)
    } else {
      updateProductCategory(data as CategoryUpdateInput)
    }
  })
  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const name = watch("name")
  const sameName = originalName ? normalizeToUpperCase(name) === originalName : false

  const canSubmit = isValid && !sameName

  return (
    <Dialog open={true} onClose={handleCancel} maxWidth='sm' fullWidth data-testid='category-dialog'>
      {isCancelConfirmationOpen ? (
        <CancelTemplate onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
      ) : (
        <>
          <DialogTitle className='pl-10 font-normal text-primary'>
            {isCreateMode ? "New" : "Edit"} Product Category
          </DialogTitle>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <CategoryForm originalName={originalName} onSubmit={onSubmit} />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel} data-testid='cancel-button'>
              Cancel
            </Button>
            <Button
              appearance='contained'
              onClick={onSubmit}
              type='submit'
              disabled={!canSubmit}
              data-testid='submit-button'
            >
              {isCreateMode ? "Save" : "Update"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default CategoryFormDialog
